import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

/* ============================================================
   REGISTOS TARDIOS — contagem partilhada

   Despesas de cartão carregadas depois de o mês da fatura já ter sido extraído
   e reconciliado. A contagem vive aqui, acima das rotas, por duas razões:
   o indicador da barra lateral tem de estar preenchido em qualquer ecrã, e o
   ecrã das Despesas precisa de a invalidar quando se marca um mês como tratado
   — se cada um fizesse o seu pedido, ficariam dois estados a divergir.

   Sem polling: a aplicação não tem um único setInterval, e não vale a pena
   introduzir o primeiro por causa disto. Segue-se o padrão já usado na Gestão
   Financeira — recarregar quando o separador volta a ficar visível ou ganha
   foco, que é quando o utilizador pode ter perdido novidades.
   ============================================================ */

const TardiosContext = createContext({
    meses: [], totalMeses: 0, totalDespesas: 0,
    carregando: false, recarregar: () => {}, marcarTratado: async () => {},
});

export const useTardios = () => useContext(TardiosContext);

const DIA_FECHO_PADRAO = 6;

/* Janela por omissão: desde o primeiro dia do mês anterior.
   Vive aqui, e não em cada ecrã, porque o contador da barra lateral e a lista
   das Despesas TÊM de olhar para o mesmo período — quando divergiam, o menu
   anunciava meses que a lista não mostrava e o aviso passava a ruído. */
export const janelaPadraoDesde = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
};

export const TardiosProvider = ({ children }) => {
    const { isAuthenticated, loading: authLoading, hasPermission } = useAuth();
    const [meses, setMeses] = useState([]);
    const [carregando, setCarregando] = useState(false);

    // Sem isto, as rotas públicas (consulta por token, onboarding) fariam este
    // pedido sem token: o interceptor do axios trata QUALQUER 401 como sessão
    // expirada e atira o visitante para o ecrã de login.
    const podeConsultar = !authLoading && isAuthenticated
        && (typeof hasPermission !== 'function' || hasPermission('despesas'));

    const buscar = useCallback(async (silencioso = false) => {
        if (!podeConsultar) return;
        if (!silencioso) setCarregando(true);
        try {
            const r = await api.get('/despesas/tardios-resumo', {
                params: { dia_fecho: DIA_FECHO_PADRAO, desde: janelaPadraoDesde() },
            });
            setMeses(r.data?.meses || []);
        } catch {
            // silencioso de propósito: é um indicador acessório, nunca deve
            // interromper o que o utilizador está a fazer
        } finally {
            if (!silencioso) setCarregando(false);
        }
    }, [podeConsultar]);

    useEffect(() => {
        if (!podeConsultar) { setMeses([]); return; }
        buscar();
        const aoVoltar = () => { if (document.visibilityState === 'visible') buscar(true); };
        document.addEventListener('visibilitychange', aoVoltar);
        window.addEventListener('focus', aoVoltar);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltar);
            window.removeEventListener('focus', aoVoltar);
        };
    }, [podeConsultar, buscar]);

    const marcarTratado = useCallback(async (ano, mes) => {
        await api.post('/despesas/tardios-tratado', { ano, mes });
        await buscar(true);
    }, [buscar]);

    return (
        <TardiosContext.Provider value={{
            meses,
            totalMeses: meses.length,
            totalDespesas: meses.reduce((s, m) => s + (m.total || 0), 0),
            carregando,
            recarregar: buscar,
            marcarTratado,
            diaFecho: DIA_FECHO_PADRAO,
            desdePadrao: janelaPadraoDesde(),
        }}>
            {children}
        </TardiosContext.Provider>
    );
};

export default TardiosContext;
